import { useState, useEffect } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useOffersStore } from "@/data/offersStore";
import { Lightbulb, Save, X, Edit2, Send } from "lucide-react";

export default function AdminOffersPage() {
  const { offers, isLoaded, fetchOffers, create, update, delete: remove, publish } = useOffersStore();
  
  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [subtitle, setSubtitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("all");

  const resetForm = () => {
    setEditingId(null);
    setTitle("");
    setSubtitle("");
    setBody("");
    setTarget("all");
    setShowForm(false);
  };

  const startEdit = (offer: any) => {
    setEditingId(offer.id);
    setTitle(offer.title);
    setSubtitle(offer.subtitle ?? "");
    setBody(offer.body);
    setTarget(offer.target);
    setShowForm(true);
  };

  const handleSave = () => {
    if (!title.trim() || !body.trim()) {
      toast.error("Le titre et le contenu sont obligatoires.");
      return;
    }
    const payload = { title, subtitle: subtitle.trim() || undefined, body, target };
    if (editingId) {
      update(editingId, payload);
      toast.success("Offre mise à jour.");
    } else {
      create(payload);
      toast.success("Nouvelle offre créée.");
    }
    resetForm();
  };

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6">
      <Card className="border-l-8 border-primary/30 shadow-sm">
        <CardHeader className="bg-primary/5 flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-primary" />
          <CardTitle className="text-2xl">Gestion des Offres Partenaires</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Liste des offres */}
          {!isLoaded ? (
            <p className="text-muted-foreground">Chargement des offres...</p>
          ) : offers.length === 0 ? (
            <p className="text-muted-foreground">Aucune offre n’est encore créée.</p>
          ) : (
            <div className="grid gap-4">
              {offers.map((off) => (
                <Card key={off.id} className="border-l-4 border-primary/30">
                  <CardHeader className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
                    <div>
                      <CardTitle>{off.title}</CardTitle>
                      {off.subtitle && <CardDescription>{off.subtitle}</CardDescription>}
                      <p className="text-xs text-muted-foreground mt-1">
                        Cible : {off.target === "all" ? "Tous les partenaires" : off.target}
                      </p>
                    </div>
                    <div className="flex gap-2 mt-2 md:mt-0">
                      <Button size="sm" variant="outline" onClick={() => startEdit(off)}>
                        <Edit2 className="w-4 h-4 mr-1" /> Modifier
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => { remove(off.id); toast.info("Offre supprimée"); }}>
                        <X className="w-4 h-4 mr-1" /> Supprimer
                      </Button>
                      {off.status === "draft" && (
                        <Button size="sm" onClick={() => { publish(off.id); toast.success("Offre publi"); }}>
                          Publier
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
          {/* Formulaire */}
          {showForm && (
            <Card className="mt-6 border-primary/30">
              <CardHeader>
                <CardTitle>{editingId ? "Modifier l’offre" : "Créer une nouvelle offre"}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <label className="w-24 font-medium">Cible</label>
                  <select value={target} onChange={(e) => setTarget(e.target.value)} className="border rounded px-2 py-1">
                    <option value="all">Tous les partenaires</option>
                    <option value="parti">Partis Politiques</option>
                    <option value="media">Médias</option>
                    <option value="ong">ONG / Associations</option>
                    <option value="institution">Institutions</option>
                    <option value="sap">SAP</option>
                  </select>
                </div>
                <div>
                  <label className="block font-medium mb-1">Titre *</label>
                  <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre de l’offre" />
                </div>
                <div>
                  <label className="block font-medium mb-1">Sous‑titre (optionnel)</label>
                  <Input value={subtitle} onChange={(e) => setSubtitle(e.target.value)} placeholder="Tagline courte" />
                </div>
                <div>
                  <label className="block font-medium mb-1">Contenu *</label>
                  <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder="Texte détaillé, markdown ou HTML" />
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={handleSave} className="gap-2">
                    <Save className="w-4 h-4" /> Enregistrer
                  </Button>
                  <Button variant="outline" onClick={resetForm}>Annuler</Button>
                </div>
              </CardContent>
            </Card>
          )}
          {!showForm && (
            <Button onClick={() => setShowForm(true)} className="mt-4 gap-2">
              <Send className="w-4 h-4" /> Créer une offre
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
